/**
 * Minimal, fast, dependency-free NBT reader (Java edition, big-endian).
 *
 * Compounds become plain objects, lists become arrays.
 * Long arrays are returned as `LongArray` (hi/lo Uint32 pairs) because
 * BigInt is far too slow for unpacking block-state data.
 */

export class LongArray {
  /** [hi0, lo0, hi1, lo1, ...] */
  readonly words: Uint32Array;
  constructor(words: Uint32Array) {
    this.words = words;
  }
  get length() {
    return this.words.length >> 1;
  }
}

export type NbtValue =
  | number
  | bigint
  | string
  | Int8Array
  | Int32Array
  | LongArray
  | NbtValue[]
  | NbtCompound;
export interface NbtCompound {
  [key: string]: NbtValue;
}

const td = new TextDecoder("utf-8");

class Reader {
  private view: DataView;
  pos = 0;
  private buf: Uint8Array;
  constructor(buf: Uint8Array) {
    this.buf = buf;
    this.view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  }
  u8() {
    return this.buf[this.pos++];
  }
  i8() {
    return this.view.getInt8(this.pos++);
  }
  i16() {
    const v = this.view.getInt16(this.pos);
    this.pos += 2;
    return v;
  }
  u16() {
    const v = this.view.getUint16(this.pos);
    this.pos += 2;
    return v;
  }
  i32() {
    const v = this.view.getInt32(this.pos);
    this.pos += 4;
    return v;
  }
  u32() {
    const v = this.view.getUint32(this.pos);
    this.pos += 4;
    return v;
  }
  f32() {
    const v = this.view.getFloat32(this.pos);
    this.pos += 4;
    return v;
  }
  f64() {
    const v = this.view.getFloat64(this.pos);
    this.pos += 8;
    return v;
  }
  str() {
    const n = this.u16();
    // Fast path for ASCII (almost all keys / block names)
    let ascii = true;
    for (let i = 0; i < n; i++) if (this.buf[this.pos + i] > 127) { ascii = false; break; }
    let s: string;
    if (ascii) {
      s = "";
      for (let i = 0; i < n; i++) s += String.fromCharCode(this.buf[this.pos + i]);
    } else {
      // Java "modified UTF-8" is close enough to UTF-8 for our purposes
      s = td.decode(this.buf.subarray(this.pos, this.pos + n));
    }
    this.pos += n;
    return s;
  }

  payload(type: number): NbtValue {
    switch (type) {
      case 1: return this.i8();
      case 2: return this.i16();
      case 3: return this.i32();
      case 4: {
        const hi = this.i32();
        const lo = this.u32();
        // exact as number up to 2^53, otherwise BigInt (e.g. world seeds)
        if (hi > 0x1fffff || hi < -0x200000) return (BigInt(hi) << 32n) | BigInt(lo);
        return hi * 4294967296 + lo;
      }
      case 5: return this.f32();
      case 6: return this.f64();
      case 7: {
        const n = this.i32();
        const a = new Int8Array(this.buf.buffer.slice(this.buf.byteOffset + this.pos, this.buf.byteOffset + this.pos + n));
        this.pos += n;
        return a;
      }
      case 8: return this.str();
      case 9: {
        const et = this.u8();
        const n = this.i32();
        const out: NbtValue[] = new Array(Math.max(0, n));
        for (let i = 0; i < n; i++) out[i] = this.payload(et);
        return out;
      }
      case 10: {
        const obj: NbtCompound = {};
        for (;;) {
          const t = this.u8();
          if (t === 0) return obj;
          const k = this.str();
          obj[k] = this.payload(t);
        }
      }
      case 11: {
        const n = this.i32();
        const a = new Int32Array(n);
        for (let i = 0; i < n; i++) a[i] = this.i32();
        return a;
      }
      case 12: {
        const n = this.i32();
        const w = new Uint32Array(n * 2);
        for (let i = 0; i < n * 2; i++) w[i] = this.u32();
        return new LongArray(w);
      }
      default:
        throw new Error(`Unknown NBT tag type ${type} at ${this.pos}`);
    }
  }
}

/** Parse uncompressed NBT bytes. Returns the root compound. */
export function parseNbt(bytes: Uint8Array): NbtCompound {
  const r = new Reader(bytes);
  const t = r.u8();
  if (t !== 10) throw new Error("NBT root is not a compound");
  r.str();
  return r.payload(10) as NbtCompound;
}

export async function inflate(bytes: Uint8Array, format: "gzip" | "deflate" | "deflate-raw"): Promise<Uint8Array> {
  const ds = new DecompressionStream(format);
  const stream = new Blob([bytes as BlobPart]).stream().pipeThrough(ds);
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

/** Parse an NBT file that may be gzip-compressed (level.dat, *.dat). */
export async function parseNbtFile(bytes: Uint8Array): Promise<NbtCompound> {
  if (bytes[0] === 0x1f && bytes[1] === 0x8b) bytes = await inflate(bytes, "gzip");
  else if (bytes[0] === 0x78) bytes = await inflate(bytes, "deflate");
  return parseNbt(bytes);
}

/** Read `count` packed values of `bits` each from a (non-spanning, 1.16+) long array. */
export function unpackLongs(arr: LongArray, bits: number, count: number, out: Uint16Array = new Uint16Array(count)): Uint16Array {
  const w = arr.words;
  const perLong = Math.floor(64 / bits);
  const mask = (1 << bits) - 1;
  let i = 0;
  for (let l = 0; l < arr.length && i < count; l++) {
    const hi = w[l * 2];
    const lo = w[l * 2 + 1];
    for (let j = 0; j < perLong && i < count; j++, i++) {
      const off = j * bits;
      let v: number;
      if (off + bits <= 32) v = (lo >>> off) & mask;
      else if (off >= 32) v = (hi >>> (off - 32)) & mask;
      else v = ((lo >>> off) | (hi << (32 - off))) & mask;
      out[i] = v;
    }
  }
  return out;
}
